import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import {
  ChangeEvent,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/lib/v2/components/Button';
import { Checkbox } from '@/lib/v2/components/Checkbox';
import { Input } from '@/lib/v2/components/Input';

import DropdownItem, { DropdownItemProps } from './DropdownItem';

export interface DropdownProps {
  id?: string | number;
  dropdownItems: DropdownItemProps[];
  label: string;
  menuOnTheLeft?: boolean;
  color?: 'standard' | 'primary';
  icon?: JSX.Element;
  fullWidth?: boolean;
  tooltip?: string;
  withSearch?: boolean;
  searchPlaceholder?: string;
  checkBox?: boolean;
  buttonText?: string;
  checkboxContainerWithScroll?: boolean;
  buttonOnClick?: (checkedItems: DropdownItemProps[]) => void;
}

const Dropdown = ({
  id,
  dropdownItems,
  label,
  menuOnTheLeft = false,
  color = 'standard',
  icon,
  fullWidth = false,
  tooltip,
  withSearch,
  searchPlaceholder,
  checkBox = false,
  buttonText,
  buttonOnClick,
  checkboxContainerWithScroll,
}: DropdownProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchValueDebounce, setSearchValueDebounce] = useState('');
  const [filteredItems, setFilteredItems] = useState<DropdownItemProps[]>();
  const [checkedItems, setCheckedItems] = useState<string[]>([]);

  const inputSearchRef = useRef<HTMLLinkElement>();

  const classesMenuItems = classNames(
    'absolute z-20 mt-2 w-56 origin-top-right rounded-md bg-white shadow-md ring-1 ring-black/5 focus:outline-none p-4',
    {
      'right-0': menuOnTheLeft,
      'py-0': !checkBox && !withSearch,
    }
  );

  const classesDropdownButton = classNames(
    'flex h-[38px] text-14 inline-flex w-full justify-center items-center gap-x-1.5 rounded-md  px-4 py-2 font-medium text-emblue-subheadings shadow-sm ring-1 ring-inset',
    { 'bg-white ring-gray-300 hover:bg-gray-50': color === 'standard' },
    {
      'bg-emblue-primary text-white ring-emblue-primary hover:bg-emblue-primary-hover hover:ring-emblue-primary-hover':
        color === 'primary',
    }
  );

  const classesArrowIcon = classNames('h-5 w-5', {
    'text-emblue-subheadings': color === 'standard',
    'text-white': color === 'primary',
  });

  const itemsMemo = useMemo(() => {
    return dropdownItems;
  }, [dropdownItems]);

  const handleOnChangeSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const handleOnChangeCheckbox = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { checked, id } = e.target;
    setCheckedItems((prevCheckedItems) => {
      const newCheckedItems = [...prevCheckedItems];
      if (checked) {
        newCheckedItems.push(id);
        return newCheckedItems;
      } else {
        return newCheckedItems.filter((checkedItem) => checkedItem !== id);
      }
    });
  }, []);

  const handleOnClickButton = useCallback(() => {
    const checkedDropdownItems = itemsMemo.filter((item) => {
      return checkedItems.some((checkedItem) => checkedItem === item.id.toString());
    });

    buttonOnClick?.(checkedDropdownItems);
  }, [buttonOnClick, checkedItems, itemsMemo]);

  const filterItems = useCallback(() => {
    return itemsMemo.filter((item) => {
      return item.label.toLowerCase().includes(searchValueDebounce.toLowerCase());
    });
  }, [itemsMemo, searchValueDebounce]);

  const autoFocusSearch = useCallback(() => {
    inputSearchRef.current?.focus();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValueDebounce(searchValue);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue]);

  useEffect(() => {
    const filteredItemsResult = filterItems();
    setFilteredItems(filteredItemsResult);
  }, [filterItems, searchValueDebounce]);

  const isSearch = searchValueDebounce.length > 0;

  return (
    <Menu
      as="div"
      className={classNames('relative inline-block text-left', { 'w-full': fullWidth })}
      data-testid="dropdown-component"
      id={String(id)}
    >
      <div>
        <Menu.Button className={classesDropdownButton} title={tooltip}>
          {icon}
          {label}
          <ChevronDownIcon aria-hidden="true" className={classesArrowIcon} />
        </Menu.Button>
      </div>

      <Transition
        afterEnter={autoFocusSearch}
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items static className={classesMenuItems}>
          {withSearch && (
            <Input
              ref={inputSearchRef}
              placeHolder={searchPlaceholder}
              value={searchValue}
              onChange={handleOnChangeSearch}
            />
          )}
          <div
            className={
              checkBox && checkboxContainerWithScroll
                ? 'visible-scrollbar mb-2 max-h-[150px] overflow-y-scroll'
                : ''
            }
          >
            {(isSearch ? filteredItems : itemsMemo)?.map((dropdownItem) => {
              const { id, label, value } = dropdownItem;

              if (checkBox) {
                const isChecked = checkedItems.some(
                  (checkedItem) => checkedItem === dropdownItem.id.toString()
                );

                return (
                  <div key={id} className="my-2 first:!mt-0">
                    <Checkbox
                      checked={isChecked}
                      id={id.toString()}
                      label={label}
                      value={value}
                      onChange={handleOnChangeCheckbox}
                    />
                  </div>
                );
              }
              return <DropdownItem key={id} {...dropdownItem} />;
            })}
          </div>
          {checkBox && buttonText && (
            <div className="flex w-full justify-end">
              <Button onClick={handleOnClickButton}>{buttonText}</Button>
            </div>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default memo(Dropdown);
